import { Box, Typography, Pagination, Stack } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import axiosClient from '../../../../../../../../lib/axios';
import LoadingApiQuery from '../../../../../../../../components/LoadingApiQuery';
import { IDataProdutosPedidos } from '../../../../../../../../types/api/comercial/pedidos-pesquisar-produtos';
import DialogAddProduto from '../DialogAddProduto';
import { IDataSetores } from '../../../../../../../../types/api/estoque/pesquisar-setores';
import { IDataDepartamentosEstoque } from '../../../../../../../../types/api/estoque/pesquisar-departamentos-estoque';
import { IDataIngredientesAtivos } from "../../../../../../../../types/api/estoque/pesquisar-ingredientes-ativos";
import { IDataPublicos } from "../../../../../../../../types/api/geral/pesquisar-publicos";
import { enqueueSnackbar } from "notistack";
import { ProdutosListagem } from './ProdutosListagem';
import { ICarrinhoItem } from '../..';

export default function Produtos({ 
    filters, 
    keyword, 
    onAddItem, 
    defaultValueCliente,
    viewMode
}: ProdutosProps) {

    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<IDataProdutosPedidos | null>(null);
    const [page, setPage] = useState(1);
    const [totalPag, setTotalPag] = useState(0);
    const publico_fazendeiro = !!defaultValueCliente?.selected_fazenda;

    const { data, isLoading } = useQuery({
        queryKey: ["/comercial/pedidos/pesquisar-produtos", keyword, page, filters],
        queryFn: () => axiosClient.get("/comercial/pedidos/pesquisar-produtos", {
            params: {
                limit: 16,
                pesquisar: keyword,
                page: page,
                departamento_id: filters.departamentos?.map(dpto => dpto.id),
                ingrediente_id: filters.ingredientes?.map(ingred => ingred.id),
                setor_id: filters.setores?.map(setor => setor.id),
                categoria_id: filters.categoria?.id,
                exibir_apenas_com_estoque: filters.exibir_apenas_com_estoque,
                possui_fazenda: publico_fazendeiro ? 1 : 0,
                tipo_publico: defaultValueCliente?.tipo_publico || null
            }
        }),
        cacheTime: 0,
        staleTime: 0
    });

    const produtos: IDataProdutosPedidos[] = useMemo(() => {
        if (!data) return []

        const totalPag = data?.data ? Math.ceil(data.data.total / data.data.per_page) : 0
        setTotalPag(totalPag)

        return data?.data.data || []
    }, [data])

    const handleAddClick = (produto: IDataProdutosPedidos) => {
        if ((produto.estoque[0]?.emite_receita === "S" && !publico_fazendeiro)) {
            enqueueSnackbar(`O produto é exclusivo para produtores rurais`, { variant: "error" })
            return
        }
        setSelectedProduct(produto);
        setDialogOpen(true);
    };

    useEffect(() => {
        setPage(1)
    }, [keyword, filters])

    return (
        <Box>
            {isLoading && (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                    <LoadingApiQuery
                        extraParams={{
                            height: 300,
                            alignItems: 'center'
                        }}
                    />
                </Box>
            )}

            {(produtos.length > 0 && !isLoading) && (
                <ProdutosListagem 
                    {...{produtos, viewMode, handleAddClick, publico_fazendeiro}}
                />
            )}

            {(produtos.length === 0 && !isLoading) && (
                <Box display="flex" justifyContent="center" alignItems="center" height={200}>
                    <Typography variant="subtitle1" color="text.secondary">Nenhum produto encontrado</Typography>
                </Box>
            )}

            <Stack mt={2} display="flex" alignItems="flex-end">
                <Pagination
                    page={page}
                    count={totalPag}
                    color='primary'
                    disabled={isLoading}
                    onChange={(_, newPage) => setPage(newPage)}
                />
            </Stack>

            {selectedProduct && (
                <DialogAddProduto
                    open={dialogOpen}
                    onClose={() => {
                        setDialogOpen(false);
                        setSelectedProduct(null);
                    }}
                    selectedProduct={selectedProduct}
                    onAddItem={onAddItem}
                />
            )}
        </Box>
    );
}

export interface ProdutosProps {
    filters: {
        pesquisar?: string;
        departamentos?: IDataDepartamentosEstoque[];
        setores?: IDataSetores[];
        ingredientes?: IDataIngredientesAtivos[];
        categoria?: {
            id: number;
            hierarquia: string;
        };
        exibir_apenas_com_estoque: boolean;
    };
    defaultValueCliente?: IDataPublicos;
    keyword: string;
    onAddItem: (item: ICarrinhoItem) => void;
    viewMode: 'grid' | 'list';
}